import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Avatar,
  TextField,
  InputAdornment,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
  Tooltip,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Sort as SortIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  LinkedIn as LinkedInIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Work as WorkIcon,
  Notes as NotesIcon,
  ContactMail as ContactMailIcon,
  ViewModule as ViewModuleIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { PotentialCandidate } from '../../types';
import airtableService from '../../services/airtable';
import PotentialCandidatesPrintView from '../../components/PotentialCandidatesPrintView';

const PotentialCandidatesList: React.FC = () => {
  const [candidates, setCandidates] = useState<PotentialCandidate[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAnchor, setFilterAnchor] = useState<null | HTMLElement>(null);
  const [sortAnchor, setSortAnchor] = useState<null | HTMLElement>(null);
  const [printAnchor, setPrintAnchor] = useState<null | HTMLElement>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('candidateName');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [groupBy, setGroupBy] = useState<'none' | 'openingStatus' | 'location' | 'okToContact'>('none');

  useEffect(() => {
    fetchPotentialCandidates();
  }, []);

  const fetchPotentialCandidates = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await airtableService.getPotentialCandidates();

      if (response.success) {
        setCandidates(response.data);
      } else {
        setError(response.error || 'Failed to fetch potential candidates');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch potential candidates');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string | undefined) => {
    if (!status) return 'default';
    switch (status) {
      case '00-interested': return 'success';
      case '01-not-interested': return 'error';
      case '02-pending': return 'warning';
      case '03-contacted': return 'info';
      default: return 'default';
    }
  };

  const filteredAndSortedCandidates = candidates
    .filter(candidate => {
      const matchesSearch = `${candidate.candidateName} ${candidate.email || ''} ${candidate.company || ''} ${candidate.title || ''}`.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = !statusFilter || candidate.currentStatus === statusFilter;
      return matchesSearch && matchesStatus;
    })
    .sort((a, b) => {
      let aValue: any;
      let bValue: any;

      switch (sortBy) {
        case 'candidateName':
          aValue = a.candidateName?.toLowerCase() || '';
          bValue = b.candidateName?.toLowerCase() || '';
          break;
        case 'company':
          aValue = a.company?.toLowerCase() || '';
          bValue = b.company?.toLowerCase() || '';
          break;
        case 'location':
          aValue = a.location?.toLowerCase() || '';
          bValue = b.location?.toLowerCase() || '';
          break;
        case 'level':
          aValue = a.level?.toLowerCase() || '';
          bValue = b.level?.toLowerCase() || '';
          break;
        default:
          aValue = a.candidateName?.toLowerCase() || '';
          bValue = b.candidateName?.toLowerCase() || '';
      }

      if (sortOrder === 'asc') {
        return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
      } else {
        return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
      }
    });

  // Group candidates based on selected groupBy option
  const groupCandidates = (candidates: PotentialCandidate[]) => {
    if (groupBy === 'none') {
      return { 'All Candidates': candidates };
    }

    const grouped: Record<string, PotentialCandidate[]> = {};

    candidates.forEach(candidate => {
      let groupKey: string;

      switch (groupBy) {
        case 'openingStatus':
          groupKey = candidate.openingStatus || 'No Opening Status';
          break;
        case 'location':
          groupKey = candidate.location || 'No Location';
          break;
        case 'okToContact':
          groupKey = candidate.okToContact ? 'OK to Contact' : 'Do Not Contact';
          break;
        default:
          groupKey = 'All Candidates';
      }

      if (!grouped[groupKey]) {
        grouped[groupKey] = [];
      }
      grouped[groupKey].push(candidate);
    });

    // Sort groups alphabetically
    const sortedGroups: Record<string, PotentialCandidate[]> = {};
    Object.keys(grouped).sort().forEach(key => {
      sortedGroups[key] = grouped[key];
    });

    return sortedGroups;
  };

  const groupedCandidates = groupCandidates(filteredAndSortedCandidates);

  const handleFilterClick = (event: React.MouseEvent<HTMLElement>) => {
    setFilterAnchor(event.currentTarget);
  };

  const handleFilterClose = () => {
    setFilterAnchor(null);
  };

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status);
    setFilterAnchor(null);
  };

  const handleSortClick = (event: React.MouseEvent<HTMLElement>) => {
    setSortAnchor(event.currentTarget);
  };

  const handleSortClose = () => {
    setSortAnchor(null);
  };

  const handleSortChange = (field: string, order: 'asc' | 'desc') => {
    setSortBy(field);
    setSortOrder(order);
    setSortAnchor(null);
  };

  const handlePrintClick = (event: React.MouseEvent<HTMLElement>) => {
    setPrintAnchor(event.currentTarget);
  };

  const handlePrintClose = () => {
    setPrintAnchor(null);
  };

  const handlePrint = (printGroupBy?: 'none' | 'openingStatus' | 'location' | 'okToContact') => {
    // Close menu
    setPrintAnchor(null);

    // If specific groupBy requested, temporarily change it
    if (printGroupBy !== undefined && printGroupBy !== groupBy) {
      const originalGroupBy = groupBy;
      setGroupBy(printGroupBy);

      // Wait for state update and re-render
      setTimeout(() => {
        window.print();

        // Restore original groupBy after print
        setTimeout(() => {
          setGroupBy(originalGroupBy);
        }, 500);
      }, 300);
    } else {
      // Print with current groupBy
      setTimeout(() => {
        window.print();
      }, 100);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Typography variant="h6">
          Unable to load potential candidates. Please check your Airtable configuration.
        </Typography>
        <button onClick={fetchPotentialCandidates} style={{ marginTop: '16px' }}>
          Try Again
        </button>
      </Box>
    );
  }

  return (
    <Box>
      <Box className="screen-only-content">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          Potential Candidates ({filteredAndSortedCandidates.length})
        </Typography>
        <Tooltip title="Print Report">
          <IconButton
            onClick={handlePrintClick}
            color="primary"
            size="large"
            sx={{
              backgroundColor: 'primary.light',
              '&:hover': {
                backgroundColor: 'primary.main',
                color: 'white',
              },
            }}
          >
            <PrintIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Search, Filter, Sort, and Group By */}
      <Box display="flex" gap={2} mb={3} alignItems="center" flexWrap="wrap">
        <TextField
          placeholder="Search potential candidates..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ flexGrow: 1, minWidth: '250px' }}
        />
        <IconButton onClick={handleFilterClick}>
          <FilterIcon />
        </IconButton>
        <IconButton onClick={handleSortClick}>
          <SortIcon />
        </IconButton>

        {/* Group By Toggle */}
        <Box display="flex" alignItems="center" gap={1}>
          <Typography variant="body2" color="text.secondary">Group By:</Typography>
          <ToggleButtonGroup
            value={groupBy}
            exclusive
            onChange={(e, newValue) => {
              if (newValue !== null) {
                setGroupBy(newValue);
              }
            }}
            size="small"
          >
            <ToggleButton value="none">
              <Tooltip title="No Grouping">
                <ViewModuleIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="openingStatus">
              <Tooltip title="Group by Opening Status">
                <WorkIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="location">
              <Tooltip title="Group by Location">
                <LocationIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
            <ToggleButton value="okToContact">
              <Tooltip title="Group by Contact Status">
                <ContactMailIcon fontSize="small" />
              </Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Menu
          anchorEl={filterAnchor}
          open={Boolean(filterAnchor)}
          onClose={handleFilterClose}
        >
          <MenuItem onClick={() => handleStatusFilter('')}>All Status</MenuItem>
          <MenuItem onClick={() => handleStatusFilter('00-interested')}>Interested</MenuItem>
          <MenuItem onClick={() => handleStatusFilter('01-not-interested')}>Not Interested</MenuItem>
          <MenuItem onClick={() => handleStatusFilter('02-pending')}>Pending</MenuItem>
          <MenuItem onClick={() => handleStatusFilter('03-contacted')}>Contacted</MenuItem>
        </Menu>
        <Menu
          anchorEl={sortAnchor}
          open={Boolean(sortAnchor)}
          onClose={handleSortClose}
        >
          <MenuItem onClick={() => handleSortChange('candidateName', 'asc')}>Name (A-Z)</MenuItem>
          <MenuItem onClick={() => handleSortChange('candidateName', 'desc')}>Name (Z-A)</MenuItem>
          <MenuItem onClick={() => handleSortChange('company', 'asc')}>Company (A-Z)</MenuItem>
          <MenuItem onClick={() => handleSortChange('company', 'desc')}>Company (Z-A)</MenuItem>
          <MenuItem onClick={() => handleSortChange('location', 'asc')}>Location (A-Z)</MenuItem>
          <MenuItem onClick={() => handleSortChange('location', 'desc')}>Location (Z-A)</MenuItem>
          <MenuItem onClick={() => handleSortChange('level', 'asc')}>Level (A-Z)</MenuItem>
          <MenuItem onClick={() => handleSortChange('level', 'desc')}>Level (Z-A)</MenuItem>
        </Menu>

        {/* Print Menu */}
        <Menu
          anchorEl={printAnchor}
          open={Boolean(printAnchor)}
          onClose={handlePrintClose}
          PaperProps={{
            sx: { minWidth: 250 }
          }}
        >
          <MenuItem disabled>
            <Typography variant="body2" fontWeight="bold" color="primary">
              Print Current View
            </Typography>
          </MenuItem>
          <MenuItem onClick={() => handlePrint()}>
            <Box display="flex" alignItems="center" width="100%">
              <PrintIcon sx={{ mr: 2, fontSize: '1.2rem' }} />
              <Box>
                <Typography variant="body2">Print as Currently Displayed</Typography>
                <Typography variant="caption" color="text.secondary">
                  {groupBy === 'none' ? 'No grouping' :
                   groupBy === 'openingStatus' ? 'Grouped by Opening Status' :
                   groupBy === 'location' ? 'Grouped by Location' :
                   'Grouped by Contact Status'}
                </Typography>
              </Box>
            </Box>
          </MenuItem>
          <Divider sx={{ my: 1 }} />
          <MenuItem disabled>
            <Typography variant="body2" fontWeight="bold" color="primary">
              Print with Grouping
            </Typography>
          </MenuItem>
          <MenuItem onClick={() => handlePrint('none')}>
            <Box display="flex" alignItems="center" width="100%">
              <ViewModuleIcon sx={{ mr: 2, fontSize: '1.2rem' }} />
              <Typography variant="body2">No Grouping</Typography>
            </Box>
          </MenuItem>
          <MenuItem onClick={() => handlePrint('openingStatus')}>
            <Box display="flex" alignItems="center" width="100%">
              <WorkIcon sx={{ mr: 2, fontSize: '1.2rem' }} />
              <Typography variant="body2">Group by Opening Status</Typography>
            </Box>
          </MenuItem>
          <MenuItem onClick={() => handlePrint('location')}>
            <Box display="flex" alignItems="center" width="100%">
              <LocationIcon sx={{ mr: 2, fontSize: '1.2rem' }} />
              <Typography variant="body2">Group by Location</Typography>
            </Box>
          </MenuItem>
          <MenuItem onClick={() => handlePrint('okToContact')}>
            <Box display="flex" alignItems="center" width="100%">
              <ContactMailIcon sx={{ mr: 2, fontSize: '1.2rem' }} />
              <Typography variant="body2">Group by Contact Status</Typography>
            </Box>
          </MenuItem>
        </Menu>

        {/* Sort Indicator */}
        {(sortBy !== 'candidateName' || sortOrder !== 'asc') && (
          <Chip
            label={`Sorted by: ${
              sortBy === 'candidateName' ? 'Name' :
              sortBy === 'company' ? 'Company' :
              sortBy === 'location' ? 'Location' :
              sortBy === 'level' ? 'Level' :
              'Name'
            } (${sortOrder === 'asc' ? 'A-Z' : 'Z-A'})`}
            variant="outlined"
            size="small"
            onDelete={() => {
              setSortBy('candidateName');
              setSortOrder('asc');
            }}
            sx={{ ml: 1 }}
          />
        )}
      </Box>

      {/* Candidates Grid - Grouped */}
      {Object.entries(groupedCandidates).map(([groupName, groupCandidates]) => (
        <Box key={groupName} mb={4}>
          {groupBy !== 'none' && (
            <Box mb={2}>
              <Typography variant="h5" color="primary" gutterBottom>
                {groupName} ({groupCandidates.length})
              </Typography>
              <Divider />
            </Box>
          )}

          <Grid container spacing={3}>
            {groupCandidates.map((candidate) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={candidate.id}>
                <Card
                  elevation={2}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { boxShadow: 4 },
                    transition: 'box-shadow 0.2s',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <CardContent sx={{
                    flexGrow: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: '500px'  // Consistent minimum height
                  }}>
                    {/* Header with Avatar and Name */}
                    <Box display="flex" alignItems="flex-start" mb={2}>
                      <Avatar
                        sx={{ bgcolor: 'primary.main', mr: 2, width: 56, height: 56 }}
                        src={candidate.candidateImage?.[0]?.thumbnails?.small?.url || candidate.candidateImage?.[0]?.url}
                      >
                        {!candidate.candidateImage?.length && <PersonIcon />}
                      </Avatar>
                      <Box flexGrow={1}>
                        <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, lineHeight: 1.2 }}>
                          {candidate.candidateName}
                        </Typography>
                        {candidate.index && (
                          <Typography variant="caption" color="text.secondary">
                            #{candidate.index}
                          </Typography>
                        )}
                      </Box>
                    </Box>

                    {/* Status Chips */}
                    <Box display="flex" gap={0.5} mb={2} flexWrap="wrap">
                      {candidate.currentStatus && (
                        <Chip
                          label={candidate.currentStatus}
                          color={getStatusColor(candidate.currentStatus) as any}
                          size="small"
                        />
                      )}
                      {candidate.level && (
                        <Chip
                          label={candidate.level}
                          variant="outlined"
                          size="small"
                        />
                      )}
                      {candidate.openingStatus && (
                        <Chip
                          label={candidate.openingStatus}
                          color="info"
                          size="small"
                        />
                      )}
                    </Box>

                    {/* Job Information */}
                    {(candidate.title || candidate.company) && (
                      <Box mb={2}>
                        {candidate.title && (
                          <Box display="flex" alignItems="center" mb={0.5}>
                            <WorkIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                              {candidate.title}
                            </Typography>
                          </Box>
                        )}
                        {candidate.company && (
                          <Box display="flex" alignItems="center" mb={0.5}>
                            <BusinessIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                              {candidate.company}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    )}

                    <Divider sx={{ my: 1 }} />

                    {/* Contact Information */}
                    <Box display="flex" flexDirection="column" gap={0.5} mb={2} sx={{ flexGrow: 1 }}>
                      {candidate.email && (
                        <Box display="flex" alignItems="center">
                          <EmailIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary', flexShrink: 0 }} />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              fontSize: '0.75rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {candidate.email}
                          </Typography>
                        </Box>
                      )}
                      {candidate.telNumber && (
                        <Box display="flex" alignItems="center">
                          <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary', flexShrink: 0 }} />
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            {candidate.telNumber}
                          </Typography>
                        </Box>
                      )}
                      {candidate.location && (
                        <Box display="flex" alignItems="center">
                          <LocationIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary', flexShrink: 0 }} />
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
                            {candidate.location}
                          </Typography>
                        </Box>
                      )}
                      {candidate.linkedinProfile && (
                        <Box display="flex" alignItems="center">
                          <LinkedInIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary', flexShrink: 0 }} />
                          <Typography
                            variant="body2"
                            color="primary"
                            component="a"
                            href={candidate.linkedinProfile}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{
                              textDecoration: 'none',
                              '&:hover': { textDecoration: 'underline' },
                              fontSize: '0.75rem'
                            }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            LinkedIn
                          </Typography>
                        </Box>
                      )}
                      {candidate.nextStepsContactEmail && (
                        <Box display="flex" alignItems="center">
                          <ContactMailIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary', flexShrink: 0 }} />
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              fontSize: '0.75rem',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {candidate.nextStepsContactEmail}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    {/* Notes Section */}
                    {(candidate.notes || candidate.generalNotes || candidate.rraNotes) && (
                      <>
                        <Divider sx={{ my: 1 }} />
                        <Box mb={1}>
                          <Box display="flex" alignItems="center" mb={0.5}>
                            <NotesIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="caption" fontWeight="bold">Notes</Typography>
                          </Box>
                          {candidate.notes && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                fontSize: '0.7rem',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                mb: 0.5
                              }}
                            >
                              {candidate.notes}
                            </Typography>
                          )}
                          {candidate.generalNotes && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                fontSize: '0.7rem',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                mb: 0.5
                              }}
                            >
                              General: {candidate.generalNotes}
                            </Typography>
                          )}
                          {candidate.rraNotes && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                fontSize: '0.7rem',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden'
                              }}
                            >
                              RRA: {candidate.rraNotes}
                            </Typography>
                          )}
                        </Box>
                      </>
                    )}

                    {/* Contact Status Footer */}
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      mt="auto"
                      pt={2}
                      borderTop="1px solid"
                      borderColor="divider"
                    >
                      <Box display="flex" alignItems="center">
                        {candidate.okToContact ? (
                          <Tooltip title="OK to Contact">
                            <CheckCircleIcon color="success" fontSize="small" />
                          </Tooltip>
                        ) : (
                          <Tooltip title="Do Not Contact">
                            <CancelIcon color="error" fontSize="small" />
                          </Tooltip>
                        )}
                        <Typography variant="caption" sx={{ ml: 1, fontSize: '0.7rem' }}>
                          {candidate.okToContact ? 'OK to Contact' : 'Do Not Contact'}
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      ))}

      {filteredAndSortedCandidates.length === 0 && (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="text.secondary">
            No potential candidates found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search criteria
          </Typography>
        </Box>
      )}
      </Box>

      {/* Print View (hidden on screen, visible when printing) */}
      {typeof document !== 'undefined' && (
        <PotentialCandidatesPrintView
          candidates={filteredAndSortedCandidates}
          groupBy={groupBy}
          groupedCandidates={groupedCandidates}
        />
      )}
    </Box>
  );
};

export default PotentialCandidatesList;
